import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { SpecialistIcon } from '../components/chatHome/specialistIcons';
import { BackLink } from '../components/ui/BackLink';
import { getPublicAgents, type PublicAgent } from '../services/agentService';
import { toSlug } from '../utils/slug';
import { useDocumentMeta } from '../hooks/useDocumentMeta';

/**
 * PUBLIC agent DIRECTORY — /agents. The crawlable hub that links to every /agent/{slug}: without it
 * the individual agent pages were sitemap-only + unlinked, which Google reports as "Discovered –
 * currently not indexed". This gives them internal link equity + a real index page. Static-first
 * (agents.json) so it prerenders with real content, no live API / CORS.
 */
const PublicAgentsPage: React.FC = () => {
  const [agents, setAgents] = useState<PublicAgent[] | null>(null);

  useEffect(() => {
    let alive = true;
    fetch('/agents.json')
      .then((r) => r.json())
      .then((d) => (d.agents?.length ? d.agents : getPublicAgents()))
      .catch(() => getPublicAgents())
      .then((list) => { if (alive) setAgents(list); })
      .catch(() => { if (alive) setAgents([]); });
    // Overlay live avatar_image (per-env DB override, absent from the static snapshot).
    getPublicAgents()
      .then((live) => {
        const byId = new Map(live.map((a) => [a.id, a.avatar_image]));
        if (alive) setAgents((prev) => prev?.map((a) => (byId.get(a.id) ? { ...a, avatar_image: byId.get(a.id) } : a)) || prev);
      })
      .catch(() => undefined);
    return () => { alive = false; };
  }, []);

  useDocumentMeta({
    title: 'Your AI production crew — Wisetree AI',
    description: 'Meet the AI crew that makes your story: directors, writers, photographers, composers, editors, and a media-legal advisor. Assemble your team and take an idea to a finished micro-drama or ad video you own.',
    canonical: 'https://wisetree.ai/agents',
    jsonLd: agents && agents.length ? {
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      name: 'Wisetree AI — Agent Directory',
      url: 'https://wisetree.ai/agents',
      hasPart: agents.map((a) => ({
        '@type': 'ProfilePage',
        name: a.name,
        url: `https://wisetree.ai/agent/${toSlug(a.name)}`,
        about: a.persona,
      })),
    } : undefined,
  });

  return (
    <div className="min-h-screen bg-white dark:bg-black text-secondary-900 dark:text-white">
      <div className="max-w-4xl mx-auto px-6 py-10">
        <BackLink to="/" label="Wisetree AI" />
        <h1 className="mt-6 text-3xl font-bold">Your AI production crew</h1>
        <p className="mt-2 text-secondary-600 dark:text-secondary-300">
          The team that makes your story — directors, writers, photographers, composers, editors, and a media-legal
          advisor, each with real craft, its own voice, and character. Assemble a crew and take an idea to a finished
          micro-drama or ad video you own.
        </p>

        {agents === null ? (
          <p className="mt-8 text-secondary-400">Loading…</p>
        ) : (
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3">
            {agents.map((a) => (
              <Link
                key={a.id}
                to={`/agent/${toSlug(a.name)}`}
                className="flex items-start gap-3 rounded-xl border border-secondary-200 dark:border-secondary-800 p-4 hover:border-secondary-400 dark:hover:border-secondary-600 transition-colors"
              >
                <span className="mt-0.5 w-9 h-9 shrink-0 rounded-full bg-secondary-100 dark:bg-secondary-900 flex items-center justify-center overflow-hidden">
                  {a.avatar_image
                    ? <img src={a.avatar_image} alt={a.name} className="w-full h-full object-cover" />
                    : <SpecialistIcon name={a.avatar_icon} className="w-5 h-5 text-secondary-600 dark:text-secondary-300" />}
                </span>
                <span className="min-w-0">
                  <span className="block font-semibold leading-tight">{a.name}</span>
                  <span className="block text-xs text-secondary-500">{a.persona}</span>
                  <span className="mt-1 block text-sm text-secondary-600 dark:text-secondary-400 line-clamp-2">{a.description}</span>
                </span>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default PublicAgentsPage;
